import { useEffect, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { supabase } from '@/lib/supabase'

/** Coach: edit an athlete's details, add their email/login later. */
export function EditAthleteDialog({ athleteId, onClose }: { athleteId: string; onClose: () => void }) {
  const qc = useQueryClient()
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [hasLogin, setHasLogin] = useState(false)
  const [setLogin, setSetLogin] = useState(false)
  const [password, setPassword] = useState('')
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)
  const [inviting, setInviting] = useState(false)
  const [inviteMsg, setInviteMsg] = useState<string | null>(null)
  const [archivedAt, setArchivedAt] = useState<string | null>(null)
  const [confirmArchive, setConfirmArchive] = useState(false)
  const [archiving, setArchiving] = useState(false)

  useEffect(() => {
    supabase
      .from('athletes')
      .select('name,email,auth_user_id,archived_at')
      .eq('id', athleteId)
      .maybeSingle()
      .then(({ data }) => {
        if (data) {
          setName(data.name ?? '')
          setEmail(data.email ?? '')
          setHasLogin(Boolean(data.auth_user_id))
          setArchivedAt(data.archived_at ?? null)
        }
        setLoading(false)
      })
  }, [athleteId])

  const sendInvite = async () => {
    setInviting(true)
    setInviteMsg(null)
    setError(null)
    try {
      // Save any email change first so the invite goes to the right address.
      const { data } = await supabase.auth.getSession()
      const token = data.session?.access_token
      if (email.trim()) {
        await fetch('/api/update-athlete', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
          body: JSON.stringify({ athleteId, email: email.trim() }),
        })
      }
      const res = await fetch('/api/invite-athlete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({ athleteId }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json.error || 'Could not send invite')
      setInviteMsg(`Invite email sent to ${json.email || email}.`)
      setHasLogin(true)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setInviting(false)
    }
  }

  // Archive hides the athlete from the roster, gym view and pickers. Nothing is
  // deleted — programs, weights and testing stay, and unarchiving restores them.
  const setArchived = async (archive: boolean) => {
    setArchiving(true)
    setError(null)
    const value = archive ? new Date().toISOString() : null
    const { error: err } = await supabase.from('athletes').update({ archived_at: value }).eq('id', athleteId)
    setArchiving(false)
    if (err) return setError(err.message)
    setArchivedAt(value)
    setConfirmArchive(false)
    await qc.invalidateQueries({ queryKey: ['athletes'] })
    await qc.invalidateQueries({ queryKey: ['testing-due'] })
  }

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      const { data } = await supabase.auth.getSession()
      const token = data.session?.access_token
      const res = await fetch('/api/update-athlete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
        body: JSON.stringify({
          athleteId,
          name: name.trim() || undefined,
          email: email.trim() || undefined,
          password: setLogin ? password : undefined,
        }),
      })
      const json = await res.json().catch(() => ({}))
      if (!res.ok) throw new Error(json.error || 'Could not save')
      await qc.invalidateQueries({ queryKey: ['athletes'] })
      await qc.invalidateQueries({ queryKey: ['athlete-access', athleteId] })
      setDone(true)
      setTimeout(onClose, 600)
    } catch (e) {
      setError((e as Error).message)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-card border border-border bg-surface p-5 shadow-card-hover animate-scale-in" onClick={(e) => e.stopPropagation()}>
        <h2 className="font-display text-2xl tracking-wide">Athlete Details</h2>

        {loading ? (
          <p className="py-6 text-center text-muted">Loading…</p>
        ) : (
          <>
            <label className="mb-1.5 mt-4 block font-condensed text-[11px] uppercase tracking-[0.2em] text-muted">Name</label>
            <Input value={name} onChange={(e) => setName(e.target.value)} />

            <label className="mb-1.5 mt-4 block font-condensed text-[11px] uppercase tracking-[0.2em] text-muted">Email</label>
            <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="athlete@email.com" />

            <div className="mt-4 rounded-btn border border-border bg-surface-2 p-3">
              <p className="text-xs text-muted">
                {hasLogin ? '✓ This athlete has a login.' : 'No login yet — they can’t sign in on their phone.'}
              </p>

              {/* Recommended: email them a link to set their own password. */}
              <Button
                variant="secondary"
                size="sm"
                className="mt-2 w-full"
                disabled={inviting || !email.trim()}
                onClick={sendInvite}
              >
                {inviting ? 'Sending…' : hasLogin ? '✉ Resend invite email' : '✉ Send invite email'}
              </Button>
              {!email.trim() && <p className="mt-1 text-[11px] text-subtle">Add an email above to invite them.</p>}
              {inviteMsg && <p className="mt-1.5 text-xs text-success">{inviteMsg}</p>}

              <p className="mt-3 border-t border-border pt-2 text-[11px] uppercase tracking-wide text-subtle">Or set a password manually</p>
              <label className="mt-2 flex items-center gap-2 text-sm text-muted">
                <input type="checkbox" checked={setLogin} onChange={(e) => setSetLogin(e.target.checked)} className="h-4 w-4 accent-gold" />
                {hasLogin ? 'Reset their password' : 'Create their login now'}
              </label>
              {setLogin && (
                <div className="mt-2 flex gap-2">
                  <Input value={password} onChange={(e) => setPassword(e.target.value)} placeholder="New password (6+ chars)" />
                  <Button variant="secondary" size="md" onClick={() => setPassword('FZ' + Math.floor(1000 + Math.random() * 9000))}>Generate</Button>
                </div>
              )}
              {setLogin && !email.trim() && <p className="mt-1.5 text-xs text-danger">Add an email above first.</p>}
            </div>

            <div className="mt-4 rounded-btn border border-border bg-surface-2 p-3">
              {archivedAt ? (
                <>
                  <p className="text-xs text-muted">
                    Archived {new Date(archivedAt).toLocaleDateString()} — hidden from the roster and gym view.
                  </p>
                  <Button variant="secondary" size="sm" className="mt-2 w-full" disabled={archiving} onClick={() => setArchived(false)}>
                    {archiving ? 'Restoring…' : 'Unarchive athlete'}
                  </Button>
                </>
              ) : confirmArchive ? (
                <>
                  <p className="text-xs text-muted">
                    Hides {name || 'this athlete'} from the roster, gym view and program assignment. Their
                    program, weights and testing are all kept, and you can unarchive any time.
                  </p>
                  <div className="mt-2 flex gap-2">
                    <Button variant="danger" size="sm" className="flex-1" disabled={archiving} onClick={() => setArchived(true)}>
                      {archiving ? 'Archiving…' : 'Yes, archive'}
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => setConfirmArchive(false)}>Cancel</Button>
                  </div>
                </>
              ) : (
                <button onClick={() => setConfirmArchive(true)} className="w-full text-left text-xs text-muted hover:text-danger">
                  Archive athlete…
                </button>
              )}
            </div>

            {error && <p className="mt-3 text-sm text-danger">{error}</p>}
            {done && <p className="mt-3 text-sm text-success">Saved.</p>}

            <div className="mt-6 flex gap-2">
              <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
              <Button
                className="flex-1"
                disabled={busy || (setLogin && (!email.trim() || password.length < 6))}
                onClick={save}
              >
                {busy ? 'Saving…' : 'Save'}
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
